import { Component, effect, inject, OnDestroy, signal } from '@angular/core';
import { FormControl, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormDatePickerComponent, FormInputComponent, FormSelectComponent, FormTextareaComponent, FormToggleSwitchComponent } from '@shared/forms';
import { MaterialListComponent, MediaImageComponent } from '@shared/components';
import { CourseFacade } from '@app/store/course';
import { FormOptionsFacade } from '@app/store/form-options';
import { COURSE_TYPE_OPTIONS, CourseType, DEFAULT_COURSE_TYPE, MediaType } from '@app/store/shared/models';
import { ModuleSelection, ModuleSelectionService, MediaSelectionService, MediaTarget } from '@app/dialogs';
import { ModuleListComponent } from '../module-list/module-list.component';

@Component({
    selector: 'app-course-add-modal',
    templateUrl: './add.component.html',
    styleUrls: ['./add.component.css'],
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        ButtonModule,
        FormInputComponent,
        FormSelectComponent,
        FormTextareaComponent,
        FormDatePickerComponent,
        FormToggleSwitchComponent,
        MediaImageComponent,
        ModuleListComponent,
        MaterialListComponent,
    ],
})
export class AddComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly courseFacade = inject(CourseFacade);
    private readonly formOptionsFacade = inject(FormOptionsFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly moduleSelectionService = inject(ModuleSelectionService);
    private readonly mediaSelectionService = inject(MediaSelectionService);
    private readonly translateService = inject(TranslateService);

    readonly saving = this.courseFacade.saving;
    readonly instructors = this.formOptionsFacade.instructors;
    readonly typeOptions = COURSE_TYPE_OPTIONS.map((option) => ({
        ...option,
        label: this.translateService.instant(option.label),
    }));

    readonly selectedModules = signal<ModuleSelection[]>([]);

    readonly minDeadline = (() => {
        const date = new Date();
        date.setDate(date.getDate() + 1);
        return date;
    })();

    readonly form = this.formBuilder.group({
        name: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        description: this.formBuilder.control(''),
        type: this.formBuilder.control<CourseType>(DEFAULT_COURSE_TYPE, [FormValidators.Required]),
        instructorId: this.formBuilder.control<string | null>(null),
        materialIds: this.formBuilder.control<string[]>([]),
        mediaPlaceholderId: new FormControl<string | null>(null),
        mediaPlaceholderPortraitId: new FormControl<string | null>(null),
        deadline: new FormControl<Date | null>(null),
        orderRestricted: this.formBuilder.control(false),
    });

    constructor() {
        if (this.formOptionsFacade.instructorsLoaded() !== true) {
            this.formOptionsFacade.getInstructors();
        }

        effect(() => {
            if (this.courseFacade.saved()) {
                this.close();
            }
        });

        effect(() => {
            if (this.courseFacade.saving() === false && this.courseFacade.saved() === false) {
                this.form.enable();
            }
        });

        effect(() => {
            const selection = this.moduleSelectionService.selected();
            if (selection) {
                this.selectedModules.update((modules) => {
                    if (modules.some((m) => m.id === selection.id)) {
                        return modules;
                    }
                    return [...modules, selection];
                });
                this.moduleSelectionService.clear();
            }
        });

        effect(() => {
            const selection = this.mediaSelectionService.selected();
            if (!selection) return;

            if (selection.target === 'materialIds') {
                const current = this.form.controls.materialIds.value ?? [];
                const id = selection.media.id;
                if (!current.includes(id)) {
                    this.form.controls.materialIds.setValue([...current, id]);
                }
            } else if (selection.target === 'mediaPlaceholderId') {
                this.form.controls.mediaPlaceholderId.setValue(selection.media.id);
            } else if (selection.target === 'mediaPlaceholderPortraitId') {
                this.form.controls.mediaPlaceholderPortraitId.setValue(selection.media.id);
            } else {
                return;
            }

            this.mediaSelectionService.clear();
        });
    }

    ngOnDestroy(): void {
        this.courseFacade.clear();
        this.formOptionsFacade.clear();
    }

    openModuleDialog(): void {
        this.router.navigate([{ outlets: { dialog: ['select-module'] } }]);
    }

    removeModule(moduleId: string): void {
        this.selectedModules.update((modules) => modules.filter((m) => m.id !== moduleId));
    }

    reorderModules(modules: ModuleSelection[]): void {
        this.selectedModules.set(modules);
    }

    onSelectMedia(target: MediaTarget, mediaType: MediaType | MediaType[]): void {
        const value = Array.isArray(mediaType) ? mediaType.join(',') : mediaType;
        this.router.navigate([{ outlets: { dialog: ['select-media', { mediaTarget: target, mediaType: value }] } }]);
    }

    onSelectMaterial(): void {
        this.onSelectMedia('materialIds', ['pdf', 'image']);
    }

    removeMaterial(id: string): void {
        const current = this.form.controls.materialIds.value ?? [];
        this.form.controls.materialIds.setValue(current.filter((mid) => mid !== id));
    }

    reorderMaterials(ids: string[]): void {
        this.form.controls.materialIds.setValue(ids);
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        if (this.form.valid) {
            this.form.disable();
            const formValue = this.form.getRawValue();
            this.courseFacade.createCourse({
                name: formValue.name,
                description: formValue.description,
                type: formValue.type,
                objectives: [],
                instructorId: formValue.instructorId,
                tagIds: [],
                moduleIds: this.selectedModules().map((m) => m.id),
                materialIds: formValue.materialIds,
                mediaPlaceholderId: formValue.mediaPlaceholderId,
                mediaPlaceholderPortraitId: formValue.mediaPlaceholderPortraitId,
                deadline: formValue.deadline,
                orderRestricted: formValue.orderRestricted,
            });
        }
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }]);
    }
}
